/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { ActionKernelType } from 'client-code/action-details';
import { ActionType } from 'client-code/actions';
import { ScreenType } from 'client-code/previews';

import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { AppUtils } from '../../AppUtils';
import { AppError } from '../../error';
import { ServicesClientImpl } from '../../interactors';
import { Log } from '../../logging';
import {
    TAction,
    TActionDetailsList,
    TActionDetailsParams,
    TActionList,
    TActionsParams,
    TVodSocuLastViewedPositionParams,
    TWatchAction,
} from '../../types';
import { UniqueIdentifier } from '../../utils';
import { IPlaybackExtraArgs } from '../../videoplayer/interfaces';
import { PlaybackContentType } from '../enums';

type VodRequiredParams = {
    id: string;
    partnerId: string;
    contentSupplierPartnerId?: string;
} & (
    | { itemType: 'offer' }
    | { itemType: 'content' | 'unifiedCollection'; collapsedVodItemId: string }
);

export class PlaybackDataModelImpl {
    private TAG: string = 'PlaybackDataModelImpl';

    private empty: any = {};

    private static oPlaybackDataModelImpl: PlaybackDataModelImpl;

    private actionDetailsData: TWatchAction = this.empty;

    private actionDetailsList: TActionDetailsList = this.empty;

    private actionListData: TActionList = this.empty;

    private newActionDetailsData: TWatchAction = this.empty;

    private newActionListData: TActionList = this.empty;

    private newPlaybackExtraParams: IPlaybackExtraArgs = this.empty;

    private offerActionError: AppError = this.empty;

    private playbackContentType: PlaybackContentType = PlaybackContentType.LIVE;

    private playbackExtraParams: IPlaybackExtraArgs = this.empty;

    private service: ServicesClientImpl = new ServicesClientImpl();

    private vodRequiredParmas: VodRequiredParams = this.empty;

    private newVodRequiredParmas: VodRequiredParams = this.empty;

    public static getInstance(): PlaybackDataModelImpl {
        if (!PlaybackDataModelImpl.oPlaybackDataModelImpl)
            PlaybackDataModelImpl.oPlaybackDataModelImpl = new PlaybackDataModelImpl();
        return PlaybackDataModelImpl.oPlaybackDataModelImpl;
    }

    public fetchActionDetails(
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        return (async () => {
            try {
                const actionDetailsResponse =
                    await this.service.fetchOttOfferActionDetails(actionDetailsParams);
                this.newActionDetailsData =
                    actionDetailsResponse?.actionDetails?.watchActionsList?.filter(
                        (action) =>
                            action.kernel.actionKernelType.toLowerCase() ===
                            ActionKernelType.WatchFromSoCuActionKernel.toLowerCase(),
                    )[0] || this.empty;
                return actionDetailsResponse;
            } catch (error: any) {
                Log.e(this.TAG, 'error', error);
                throw error;
            }
        })();
    }

    public async fetchActionList(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            const actionListResponse = await this.service.getOfferActions(actionsParams);
            this.newActionListData = actionListResponse;
            return actionListResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    public async fetchActionV2List(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            const actionListResponse = await this.service.getOfferActionsV2(actionsParams);
            this.newActionListData = actionListResponse;
            return actionListResponse;
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    public async fetchLastSavedPosition(
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<{ watchAction: TWatchAction; actionDetailsNetworkCallId?: number }> {
        const { socuOfferId, isCatchUp, isStartOver } = this.getNewSocuAction()?.kernel;
        const actionDetailsParams: TActionDetailsParams = {
            isCatchUp: !!isCatchUp,
            isStartOver: !!isStartOver,
            offerId: socuOfferId,
            screenType,
            actionDetailsNetworkCallId: networkCallId,
        };
        try {
            const { actionDetailsNetworkCallId } =
                await this.fetchActionDetails(actionDetailsParams);
            const watchAction: TWatchAction = this.getNewActionDetails();
            return { watchAction, actionDetailsNetworkCallId };
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }

    public async fetchOttContentActionDetails(
        contentId: string,
        screenType: string,
    ): Promise<Promise<TActionDetailsList>> {
        try {
            const networkCallId = UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
            const ottActionDetailsResponse = await this.service.fetchOttContentActionDetails({
                contentId,
                screenType,
            });
            if (networkCallId !== UniqueIdentifier.getOttActionDetailsNetworkCallId()) {
                throw new Error('Network call id mismatch');
            }
            this.actionDetailsList = ottActionDetailsResponse;

            return this.actionDetailsList;
        } catch (error: any) {
            Log.e(this.TAG, 'Error in fetching ottActionDetailsResponse: ', error);
            throw error;
        }
    }

    public getActionDetails(): TWatchAction {
        if (!AppUtils.isEmpty(this.actionDetailsData)) return this.actionDetailsData;
        return this.empty;
    }

    public getActionDetailsList(): TActionDetailsList {
        return this.actionDetailsList;
    }

    public getActionList(): TActionList {
        if (!AppUtils.isEmpty(this.actionListData)) return this.actionListData;
        return this.empty;
    }

    public getLastViewedPosition(): number {
        if (!AppUtils.isEmpty(this.actionDetailsData)) {
            return this.actionDetailsData.kernel.savedPosition || 0;
        }
        return 0;
    }

    public getNewActionDetails(): TWatchAction {
        if (!AppUtils.isEmpty(this.newActionDetailsData)) return this.newActionDetailsData;
        return this.empty;
    }

    public getNewActionList(): TActionList {
        if (!AppUtils.isEmpty(this.newActionListData)) return this.newActionListData;
        return this.empty;
    }

    public getNewSocuAction(): TAction {
        if (!AppUtils.isEmpty(this.newActionListData)) {
            return (
                this.newActionListData.actionList?.actions?.filter(
                    (action) => action.actionType === ActionType.WatchFromProviderAction,
                )[0] || this.empty
            );
        }
        return this.empty;
    }

    public getOfferActionError(): AppError {
        return this.offerActionError;
    }

    public getPlaybackContentType(): PlaybackContentType {
        return this.playbackContentType;
    }

    public getPlaybackExtraParams(): IPlaybackExtraArgs {
        return this.playbackExtraParams;
    }

    public getSocuAction(): TAction {
        if (!AppUtils.isEmpty(this.actionListData)) {
            return (
                this.actionListData.actionList?.actions?.filter(
                    (action) => action.actionType === ActionType.WatchFromProviderAction,
                )[0] || this.empty
            );
        }
        return this.empty;
    }

    // getters for vod required params
    public getNewVodRequiredParams(): VodRequiredParams {
        return this.newVodRequiredParmas;
    }

    public getVodRequiredParams(): VodRequiredParams {
        return this.vodRequiredParmas;
    }

    public isCatchUp(): boolean {
        return (
            this.playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
            this.playbackContentType === PlaybackContentType.CATCHUP_RESUME
        );
    }

    public isSocu(): boolean {
        return this.isCatchUp() || this.isStartOver();
    }

    public isStartOver(): boolean {
        return (
            this.playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
            this.playbackContentType === PlaybackContentType.STARTOVER_RESUME
        );
    }

    public resetNewPlaybackData(): void {
        this.newActionListData = this.empty;
        this.newActionDetailsData = this.empty;
        this.offerActionError = this.empty;
        this.newPlaybackExtraParams = this.empty;
        this.newVodRequiredParmas = this.empty;
    }

    public resetPlaybackData(): void {
        this.resetNewPlaybackData();
        this.playbackContentType = PlaybackContentType.LIVE;
        this.actionListData = this.empty;
        this.actionDetailsData = this.empty;
        this.actionDetailsList = this.empty;
        this.playbackExtraParams = this.empty;
        this.vodRequiredParmas = this.empty;
    }

    public setActionDetails(actions: TWatchAction): void {
        this.newActionDetailsData = actions;
    }

    public setActionDetailsList(list: TActionDetailsList): void {
        this.actionDetailsList = list;
    }

    public setActionListData(response: any): void {
        this.newActionListData = response;
    }

    public setOfferActionError(error: AppError) {
        this.offerActionError = error;
    }

    public setPlaybackData(playbackType: PlaybackContentType): void {
        this.actionListData = this.newActionListData;
        this.actionDetailsData = this.newActionDetailsData;
        this.playbackContentType = playbackType;
        this.playbackExtraParams = this.newPlaybackExtraParams;
        this.vodRequiredParmas = this.newVodRequiredParmas;
        this.resetNewPlaybackData();
    }

    public setPlaybackExtraParams(param: IPlaybackExtraArgs): void {
        this.newPlaybackExtraParams = param;
    }

    public setVodRequiredParams(param: VodRequiredParams): void {
        this.newVodRequiredParmas = param;
    }

    public updateLastViewedPosition(
        currentTime: number,
        startTime: number,
        screenType: string,
    ): void {
        try {
            const { collectionId, partnerOfferId, partnerId, contentId, duration } =
                this.getActionDetails()?.kernel || {};
            if (currentTime && contentId && partnerOfferId) {
                let lastViewedPosition = Math.round(currentTime / MILLISECONDS_IN_A_SECOND);
                if (this.isStartOver()) {
                    lastViewedPosition = Math.round(
                        (currentTime - startTime) / MILLISECONDS_IN_A_SECOND,
                    );
                }
                const socuLVPParams: TVodSocuLastViewedPositionParams = {
                    lastViewedPosition,
                    partnerOfferId,
                    providerPartnerId: partnerId,
                    screenType,
                    contentId,
                    collectionId,
                    duration,
                };
                this.service.setSocuLastViewedPosition(socuLVPParams);
            }
        } catch (error: any) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
